/**
 * Main-thread client for the neuroimage decode worker (#670).
 *
 * Owns the worker's lifecycle, correlates in-flight requests, and falls back to
 * decoding on the calling thread when no worker is available — jsdom has no
 * `Worker`, and a browser that refuses to construct one must still show the
 * neuroimage (with the old freeze) rather than fail the load.
 *
 * The worker's URL comes from a `<meta>` tag rather than being computed here:
 * the file is a content-hashed webpack entry, and only `webpack_manifest.json`
 * → Flask config → the page template knows its name.
 */
import { DecodedVolume, decodeVolumePayloadSync } from '@brainfocus/web/neuroimage_decode';

/** Meta tag carrying the decode worker's built URL. */
export const DECODE_WORKER_META = 'bf-decode-worker-src';

export interface DecodeResult {
    volume: DecodedVolume;
    /** Wall-clock decode duration, for the `[neuroimage]` load-phase log. */
    decodeMs: number;
    /** False when the decode ran on the calling thread through the fallback. */
    offThread: boolean;
}

export interface DecodeClientOptions {
    /**
     * Creates the worker, or returns null when one cannot be had. Throwing is
     * treated the same as returning null, with the reason reported.
     */
    workerFactory?: () => Worker | null;
    /** Called once, the first time the client latches onto the fallback. */
    onFallback?: (reason: string) => void;
}

export interface DecodeClient {
    decode(payload: ArrayBuffer): Promise<DecodeResult>;
}

interface WorkerResponse {
    id: number;
    ok: boolean;
    volume?: DecodedVolume;
    error?: string;
}

interface Pending {
    resolve: (result: DecodeResult) => void;
    reject: (error: Error) => void;
    startedAt: number;
}

/**
 * Create a decode client.
 *
 * The worker is created lazily on first decode and then reused for the page's
 * lifetime: startup is paid once, and volumes are decoded repeatedly across MRI
 * reassignments and analysis switches within the SPA. A construction failure is
 * latched, so a blocked worker is not retried on every load.
 */
export function createDecodeClient(options: DecodeClientOptions = {}): DecodeClient {
    const workerFactory = options.workerFactory ?? defaultWorkerFactory;
    const pending = new Map<number, Pending>();
    let worker: Worker | null = null;
    let workerUnavailable = false;
    let nextId = 1;

    return { decode };

    async function decode(payload: ArrayBuffer): Promise<DecodeResult> {
        const active = ensureWorker();
        if (!active) {
            return decodeOnThisThread(payload);
        }

        const id = nextId++;
        return new Promise<DecodeResult>((resolve, reject) => {
            pending.set(id, { resolve, reject, startedAt: now() });
            // The payload is transferred: nothing may read it after this call.
            active.postMessage({ id, zip: payload }, [payload]);
        });
    }

    function decodeOnThisThread(payload: ArrayBuffer): Promise<DecodeResult> {
        const startedAt = now();
        try {
            const volume = decodeVolumePayloadSync(payload);
            return Promise.resolve({ volume, decodeMs: now() - startedAt, offThread: false });
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }
    }

    function ensureWorker(): Worker | null {
        if (worker || workerUnavailable) return worker;

        try {
            worker = workerFactory();
        } catch (error) {
            worker = null;
            fallBack(error instanceof Error ? error.message : String(error));
            return null;
        }

        if (!worker) {
            fallBack(describeWorkerUnavailable());
            return null;
        }

        worker.addEventListener('message', onMessage as (ev: Event) => void);
        worker.addEventListener('error', onWorkerError as (ev: Event) => void);
        return worker;
    }

    function onMessage(event: MessageEvent<WorkerResponse>): void {
        const response = event.data;
        const entry = pending.get(response.id);
        if (!entry) return;
        pending.delete(response.id);

        if (response.ok && response.volume) {
            entry.resolve({
                volume: response.volume,
                decodeMs: now() - entry.startedAt,
                offThread: true,
            });
        } else {
            entry.reject(new Error(response.error ?? 'Neuroimage decode failed'));
        }
    }

    /**
     * A worker-level failure (script load error, uncaught throw) leaves every
     * in-flight decode unanswered, so they are rejected rather than left hanging
     * on the viewer's spinner.
     */
    function onWorkerError(event: ErrorEvent): void {
        const error = new Error(`Neuroimage decode worker failed: ${event.message || 'unknown error'}`);
        Array.from(pending.values()).forEach(entry => entry.reject(error));
        pending.clear();
    }

    function fallBack(reason: string): void {
        if (workerUnavailable) return;
        workerUnavailable = true;
        if (options.onFallback) {
            options.onFallback(reason);
        } else {
            console.warn('[neuroimage] decoding on the main thread:', reason);
        }
    }
}

/**
 * Construct the worker from the URL the page advertises. Returns null when the
 * environment has no `Worker` (jsdom) or the page carries no worker URL (a build
 * predating this entry), which sends the client down the fallback path.
 */
/**
 * Why no worker could be constructed, in terms someone debugging can act on.
 *
 * The generic "no decode worker available" this replaces cost PR #867's QA a
 * measurement: the reviewer saw the fallback, could not tell whether the browser
 * or the wiring was at fault, and reported numbers taken with the decode running
 * on the main thread. The missing-tag case is by far the most likely, so it says
 * what to do about it.
 */
function describeWorkerUnavailable(): string {
    if (typeof Worker === 'undefined') {
        return 'this browser has no Worker support';
    }
    if (typeof document === 'undefined') {
        return 'no document to read the worker URL from';
    }
    return `the page carries no ${DECODE_WORKER_META} meta tag, so the decode worker's URL never arrived`
        + ' — rebuild the frontend (cd js && npm run build, or let the watch finish) and reload the page';
}

function defaultWorkerFactory(): Worker | null {
    if (typeof Worker === 'undefined' || typeof document === 'undefined') return null;

    const src = document.querySelector(`meta[name="${DECODE_WORKER_META}"]`)?.getAttribute('content');
    if (!src) return null;

    return new Worker(src);
}

function now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

let sharedClient: DecodeClient | undefined;

/**
 * Decode a volume payload through the shared page-wide client.
 *
 * @param payload Raw zip bytes; transferred to the worker, so the caller must
 *     not read them afterwards.
 */
export function decodeVolumePayload(payload: ArrayBuffer): Promise<DecodeResult> {
    sharedClient ??= createDecodeClient();
    return sharedClient.decode(payload);
}
